import type { FunctionComponent } from 'react';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { useSearchSkillsQuery, useSkillRecommendationsQuery } from '../../app/query-hooks/skills';
import { SkillSearchContentBase } from '../../job-architecture-v2';
import type { LearningOpportunityFormState } from '../form-state';

export const LearningOpportunitySkillSearchContent: FunctionComponent<{
    readonly languageCode?: string;
    readonly onSkillAdded: () => void;
}> = ({ languageCode, onSkillAdded }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const { setValue, watch } = useFormContext<LearningOpportunityFormState>();
    const skills = watch('skills') ?? [];
    const selectedSkillIds = new Set(skills.map((skill) => skill.id));
    const skillsQuery = useSearchSkillsQuery(searchTerm, ['hard', 'soft', 'language'], 10);
    const recommendedSkillsQuery = useSkillRecommendationsQuery(skills);
    const displayedSkills = searchTerm.length > 0 ? skillsQuery.data : recommendedSkillsQuery.data;
    const isFetching = searchTerm.length > 0 ? skillsQuery.isFetching : recommendedSkillsQuery.isFetching;

    const handleSkillSelect = (skillId: string): void => {
        const skill = displayedSkills?.find((option) => option.id === skillId);
        if (skill === undefined) return;
        setValue('skills', [...skills, skill], { shouldDirty: true });
        setSearchTerm('');
        onSkillAdded();
    };

    return (
        <SkillSearchContentBase
            displayedSkills={displayedSkills}
            inputId="learning-opportunity-skill-search"
            isLoading={isFetching}
            isSkillSelected={(id) => selectedSkillIds.has(id)}
            languageCode={languageCode}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            onSkillSelect={handleSkillSelect}
        />
    );
};
